import React, { Component } from 'react';

import { Avatar, Box, Button, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import DeckGL, { IconLayer } from 'deck.gl';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import 'moment-timezone';
import { Map as ReactMapGL } from 'react-map-gl/maplibre';
import { Link, Route, Routes, useParams, useNavigate } from 'react-router-dom';
import { compose } from 'utils/compose';

import withTabActive from 'HOCs/withTabActive';
import MARKERS from '../../assets/svg/home_marker.svg';
import FooterContainer from '../../components/Footer';
import withMediaQuery from '../../constants/mediaQuery';
import * as SHIPMENT_STATUS from '../../constants/shipmentStatus';
import { EDIT_NOTICE, VERBIAGE } from '../../constants/verbiage';
import { getCookie } from '../../utils/cookie';
import { renderDeliveryTimeAndLabel, translateEvents } from '../../utils/events';
import SignatureContainer from '../signature';
import EditAddressDialog from './EditAddressDialog';
import EditInstructionDialog from './EditInstructionDialog';
import EditLocationDialog from './EditLocationDialog';
import EditSignatureOptionDialog from './EditSignatureOptDialog';
import EditWindowDialog from './EditWindowDialog';
import { useStyles } from './styles';

// HOC to inject styles into class component
// no-dd-sa:javascript-code-style/parameter-name
const withUseStyles = (WrappedComponent) => {
  return function WithStylesWrapper(props) {
    const classes = useStyles();
    return <WrappedComponent {...props} classes={classes} />;
  };
};

class EditShipmentContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showNotice: true,
      viewport: {
        latitude: 37.772402,
        longitude: -122.414265,
        zoom: 16,
      },
      mapStyle: `https://api.maptiler.com/maps/topo/style.json?key=${import.meta.env.REACT_APP_MAP_TILER_KEY}`,
    };
  }

  componentDidMount() {
    const { store, match } = this.props;
    const { deliveryStore } = store;
    const { delivery, token, editedDeliveryData } = deliveryStore;
    const queryParams = new URLSearchParams(window.location.search);

    let tokenId = queryParams.get('axl_shipment_token') || queryParams.get('t');
    if (!tokenId) {
      tokenId = getCookie('axl_shipment_token');
    }

    if (!token && !tokenId) {
      return null;
    }

    deliveryStore.getTokenInfo(tokenId, (r) => {
      if (match?.params && match.params.tracking_code) {
        deliveryStore.getDelivery(match.params.tracking_code, (res) => {
          if (res.data?.shipment && res.data.shipment.dropoff_address) {
            const { shipment } = res.data;
            this.setState({
              viewport: {
                ...this.state.viewport,
                latitude: shipment.dropoff_address.lat,
                longitude: shipment.dropoff_address.lng,
              },
            });

            // get customer edited data
            deliveryStore.getEditedDeliveryData((res) => {
              const editedDelivery = res.data;
              if (editedDelivery?.dropoff_latitude && editedDelivery.dropoff_longitude) {
                this.setState({
                  viewport: {
                    ...this.state.viewport,
                    latitude: editedDelivery.dropoff_latitude,
                    longitude: editedDelivery.dropoff_longitude,
                  },
                });
              }
            });

            // get addon services
            deliveryStore.getAddonServices();
            deliveryStore.markManageDeliveryOpened();
          }
        });
        deliveryStore.getSettings(match.params.tracking_code);
      }
    });
  }

  exitSession() {
    const { store, navigate } = this.props;

    store.deliveryStore.exitSession(() => navigate(`/${store.deliveryStore.trackingCode}/`));
  }

  isAddonEnable = (addon) => {
    const { store } = this.props;
    const { addonServices } = store.deliveryStore;

    if (_.isEmpty(addonServices)) return false;

    const addonService = _.find(addonServices, { type: addon });

    if (!addonService) return false;

    return addonService.enabled;
  };

  renderMarker = (location) => {
    return new IconLayer({
      id: 'shipment-marker',
      data: [
        {
          location: [location.lng, location.lat],
          size: 40,
          icon: 'green',
        },
      ],
      pickable: true,
      iconAtlas: MARKERS,
      iconMapping: { green: { x: 0, y: 0, width: 168, height: 224, anchorY: 224 } },
      getIcon: (d) => d.icon,
      sizeScale: 1,
      opacity: 1,
      visible: true,
      billboard: false,
      getPosition: (d) => d.location,
      getSize: (d) => d.size,
    });
  };

  renderDialog(view) {
    switch (view) {
      case 'window':
        return <EditWindowDialog />;
      case 'address':
        return <EditAddressDialog />;
      case 'note':
        return <EditInstructionDialog />;
      case 'map':
        return <EditLocationDialog />;
      case 'signature':
        return <EditSignatureOptionDialog />;
      default:
        return null;
    }
  }

  gotoSign = (e) => {
    const { navigate, match } = this.props;
    navigate('/' + match.params.tracking_code + '/signature');
  };

  render() {
    const { classes, store, navigate, mediaQuery, match } = this.props;
    const { showNotice, viewport, mapStyle } = this.state;
    const { deliveryStore } = store;
    const {
      delivery,
      settings,
      token,
      tokenRemainingTime,
      tokenRemainingTimeString,
      exitSessionDialog,
      editDialogOpened,
      dialogLoading,
      dialogErrorMsg,
      editedDeliveryData,
      loadingDelivery,
      hideCompanyBranding,
    } = deliveryStore;

    if (!token) {
      return (
        <Container maxWidth="lg" sx={classes.container}>
          <Box style={{ textAlign: 'center' }} p={5}>
            <Box py={3}>No token found! Please redeem new one.</Box>
            <Button
              variant="contained"
              color="primary"
              sx={{ ...classes.button, ...classes.dialogButton, margin: 'auto' }}
              onClick={() => navigate(`/${match.params.tracking_code}/`)}
              disableElevation
            >
              OK
            </Button>
          </Box>
        </Container>
      );
    }

    if (loadingDelivery) {
      return (
        <Container maxWidth="lg" sx={classes.container}>
          <Grid style={{ height: 300 }} container alignItems="center" justifyContent="center">
            <CircularProgress color="primary" />
          </Grid>
        </Container>
      );
    }

    if (!delivery) return null;

    const { client, shipment, outbound_events, signRequired, no_service_dates } = delivery;
    const location =
      editedDeliveryData.dropoff_latitude && editedDeliveryData.dropoff_longitude
        ? { lat: editedDeliveryData.dropoff_latitude, lng: editedDeliveryData.dropoff_longitude }
        : shipment.dropoff_address;
    const layers = [this.renderMarker(location)];
    const translatedEvents = translateEvents(outbound_events || [], shipment);
    const milestones = translatedEvents.filter((e) => VERBIAGE[e.convertedSignal]).map((e) => VERBIAGE[e.convertedSignal].milestone);
    const milestone = milestones[0] || SHIPMENT_STATUS.PROCESSING;
    const deliveryTimeAndLabel = renderDeliveryTimeAndLabel(milestone, translatedEvents, shipment, settings, no_service_dates);
    const timezone = shipment?.timezone ? shipment.timezone : 'America/Los_Angeles';
    const fromTime = moment(shipment.dropoff_earliest_ts).tz(timezone).format('hh:mm A');
    const toTime = moment(shipment.dropoff_latest_ts).tz(timezone).format('hh:mm A');

    // Get edited data
    const editedFromTs = deliveryStore.getEditedDeliveryField('dropoff_earliest_ts');
    const editedFromTime = moment(editedFromTs).tz(timezone).format('hh:mm A');
    const editedToTs = deliveryStore.getEditedDeliveryField('dropoff_latest_ts');
    const editedToTime = moment(editedToTs).tz(timezone).format('hh:mm A');

    return (
      <Container maxWidth={false} disableGutters sx={classes.container}>
        <Container sx={classes.bodyContainer}>
          <Box mb={3} sx={classes.boxWrapper}>
            <Box py={3} px={mediaQuery.isMobile ? 2 : 4} sx={classes.sessionHeader}>
              <Typography sx={{ ...classes.marginBottom, ...classes.sessionHeaderTitle }} variant="h3">
                Manage Your Delivery
              </Typography>
              <Typography sx={classes.marginBottom}>
                You have just entered a session to edit Delivery Details, please complete your request before the session time!
              </Typography>
              <Grid container justifyContent="space-between" flexWrap={'wrap'} gap={1}>
                <Grid sx={{ ...classes.selfCenter, fontSize: mediaQuery.isMobile ? 13 : undefined }}>
                  <Box component="span" sx={{ ...classes.bolded, ...classes.underlined }}>
                    Session time:
                  </Box>
                  <Box component="span" sx={classes.countdownTimer}>
                    {tokenRemainingTimeString}
                  </Box>
                </Grid>
                <Grid>
                  <Button onClick={() => deliveryStore.openExitSessionDialog()} variant="outlined" color="primary" sx={classes.button}>
                    Exit session
                  </Button>
                </Grid>
              </Grid>
            </Box>
            <Box px={mediaQuery.isMobile ? 2 : 4} sx={classes.sessionBody}>
              <Box py={3} sx={classes.shipmentDetails}>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    <Typography variant="h5" sx={{ ...classes.sessionBodyTitle, ...classes.marginBottom }}>
                      Current Shipment Details
                    </Typography>
                  </Box>
                </Box>

                <Box my={mediaQuery.isMobile ? 2 : 5}>
                  <Grid container sx={classes.shipmentDetailsContent}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Grid container>
                        {!hideCompanyBranding && (
                          <Grid>
                            <Avatar alt="Client Logo" src={client.logo_url} variant="square" sx={classes.clientLogo} />
                          </Grid>
                        )}
                        <Grid sx={{ ...classes.selfCenter, marginLeft: '5px' }}>
                          {!hideCompanyBranding && <Typography sx={classes.bolded}>{client.company}</Typography>}
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography sx={classes.grayText}>Status:</Typography>
                      <Typography
                        sx={{
                          ...classes.bolded,
                          ...(milestone === SHIPMENT_STATUS.FAILED && classes.redColor),
                        }}
                      >
                        {milestone}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography sx={classes.grayText}>{deliveryTimeAndLabel.label}</Typography>
                      <Typography sx={classes.bolded}>{deliveryTimeAndLabel.time}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              <Box py={3}>
                <Typography variant="h5" sx={{ ...classes.sessionBodyTitle, ...classes.marginBottom }}>
                  Current Delivery Details
                </Typography>
                <Typography sx={classes.noticeText}>{EDIT_NOTICE}</Typography>
                <Grid container style={{ margin: '30px auto' }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Grid container direction="column">
                      {settings && settings.enable_signature_opt_out === 'true' && (
                        <Grid sx={classes.editItem}>
                          <Grid container spacing={2} justifyContent="space-between">
                            <Grid sx={classes.selfCenter}>
                              <Typography sx={classes.editItemHeader}>Signature Option:</Typography>
                            </Grid>
                            <Grid>
                              <Button
                                sx={classes.button}
                                onClick={() => store.deliveryStore.openEditDialog('signature')}
                                variant="outlined"
                                color="primary"
                                size="small"
                              >
                                Edit
                              </Button>
                            </Grid>
                          </Grid>
                          <Box py={2} display="flex" justifyContent="space-between">
                            <Box>
                              <span style={{ fontWeight: 600 }}>
                                {(
                                  editedDeliveryData.signature_required !== undefined
                                    ? editedDeliveryData.signature_required
                                    : shipment.signature_required
                                )
                                  ? 'No-Touch delivery'
                                  : 'Not Required'}
                              </span>
                            </Box>
                            {delivery.signRequired && settings.enable_sign_in_manage_delivery === 'true' && (
                              <Box>
                                <Button sx={classes.button} onClick={this.gotoSign} variant="outlined" color="primary" size="small">
                                  Sign
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      )}
                      <Grid sx={classes.editItem}>
                        <Grid container spacing={2} justifyContent="space-between">
                          <Grid sx={classes.selfCenter}>
                            <Typography sx={classes.editItemHeader}>Delivery window:</Typography>
                          </Grid>
                          <Grid>
                            {this.isAddonEnable('customized_delivery_time') && (
                              <Button
                                sx={classes.button}
                                onClick={() => store.deliveryStore.openEditDialog('window')}
                                variant="outlined"
                                color="primary"
                                size="small"
                              >
                                Edit
                              </Button>
                            )}
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} alignItems="center">
                          <Grid size={3}>
                            <Box sx={classes.deliveryWindowLabel}>Scheduled:</Box>
                          </Grid>
                          <Grid size={9}>
                            {!editedFromTs || !editedToTs ? (
                              <span style={{ fontWeight: 600 }}>
                                {fromTime || 'N/A'} - {toTime || 'N/A'}
                              </span>
                            ) : (
                              `${fromTime || 'N/A'} - ${toTime || 'N/A'}`
                            )}
                          </Grid>
                          <Grid size={3}>
                            <Box sx={classes.deliveryWindowLabel}>Preferred:</Box>
                          </Grid>
                          <Grid size={9}>
                            {!editedFromTs || !editedToTs ? (
                              'N/A'
                            ) : (
                              <span style={{ fontWeight: 600 }}>
                                {editedFromTime} - {editedToTime}
                              </span>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid sx={classes.editItem}>
                        <Grid container spacing={2} justifyContent="space-between">
                          <Grid sx={classes.selfCenter}>
                            <Typography sx={classes.editItemHeader}>Address:</Typography>
                          </Grid>
                          <Grid>
                            <Button
                              sx={classes.button}
                              onClick={() => store.deliveryStore.openEditDialog('address')}
                              variant="outlined"
                              color="primary"
                              size="small"
                            >
                              Edit
                            </Button>
                          </Grid>
                        </Grid>
                        <Box py={1}>{shipment.dropoff_address.street}</Box>
                        <Box py={1}>
                          <span style={{ fontWeight: 600 }}>{editedDeliveryData.street2 || shipment.dropoff_address.street2 || 'N/A'}</span>
                        </Box>
                        <Box py={1}>
                          {shipment.dropoff_address.city}, {shipment.dropoff_address.state.toUpperCase()}, {shipment.dropoff_address.zipcode}
                        </Box>
                      </Grid>
                      <Grid sx={classes.editItem}>
                        <Grid container spacing={2} justifyContent="space-between">
                          <Grid sx={classes.selfCenter}>
                            <Typography sx={classes.editItemHeader}>Delivery Instructions:</Typography>
                          </Grid>
                          <Grid>
                            <Button
                              sx={classes.button}
                              onClick={() => store.deliveryStore.openEditDialog('note')}
                              variant="outlined"
                              color="primary"
                              size="small"
                            >
                              Edit
                            </Button>
                          </Grid>
                        </Grid>
                        <Box py={2}>
                          <Box>
                            <span style={{ fontWeight: 600 }}>
                              {editedDeliveryData.delivery_instruction || shipment.dropoff_additional_instruction || 'N/A'}
                            </span>
                          </Box>
                          {(editedDeliveryData.access_code || shipment.dropoff_access_code) && (
                            <Box py={2} sx={classes.editItemHeader}>
                              <span>Access code: </span>
                              <span style={{ fontWeight: 600 }}>{editedDeliveryData.access_code || shipment.dropoff_access_code || 'N/A'}</span>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }} sx={classes.locationMapWrapper}>
                    <Box p={3} sx={classes.mapEdit}>
                      <Grid container spacing={2} justifyContent="space-between">
                        <Grid sx={classes.selfCenter}>
                          <Typography sx={classes.editItemHeader}>Location Pin:</Typography>
                        </Grid>
                        <Grid>
                          <Button
                            sx={classes.button}
                            onClick={() => store.deliveryStore.openEditDialog('map')}
                            variant="outlined"
                            color="primary"
                            size="small"
                          >
                            Edit
                          </Button>
                        </Grid>
                      </Grid>
                      <Box sx={classes.locationMap} mt={2.5}>
                        <ReactMapGL {...viewport} mapStyle={mapStyle} onMove={() => {/* empty */}} style={{ width: '100%', height: '100%' }} attributionControl={false}>
                          <DeckGL layers={layers} viewState={viewport} style={{ pointerEvents: 'none' }} />
                        </ReactMapGL>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
          <Dialog
            open={editDialogOpened}
            onClose={() => store.deliveryStore.closeEditDialog()}
            maxWidth="md"
            sx={classes.container}
            slotProps={{
              paper: { sx: { padding: '16px 0', ...classes.dialogPaper } },
            }}
          >
            {this.renderDialog(editDialogOpened)}
            {dialogLoading && (
              <Box sx={classes.dialogOverlay}>
                <CircularProgress sx={classes.dialogOverlayProgress} color="primary" />
              </Box>
            )}
            {dialogErrorMsg && (
              <Box py={1} px={3}>
                <Typography align="center" sx={classes.redColor}>
                  {dialogErrorMsg}
                </Typography>
              </Box>
            )}
          </Dialog>
          <Dialog
            open={showNotice}
            onClose={() => this.setState({ showNotice: false })}
            maxWidth="lg"
            sx={classes.container}
            slotProps={{
              paper: { sx: { padding: '24px 0', textAlign: 'center', ...classes.container, ...classes.dialogPaper } },
            }}
          >
            <DialogTitle sx={classes.dialogTitle}>
              <span style={{ fontWeight: 600 }}>Your session will end soon!</span>
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ ...classes.darkGrayText, ...classes.dialogBodyText }}>
                Please make your requests within the session time before it runs out.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ ...classes.dialogActionsWrapper, justifyContent: 'center' }}>
              <Button
                onClick={() => this.setState({ showNotice: false })}
                variant="contained"
                color="primary"
                sx={{ ...classes.button, ...classes.dialogButton }}
              >
                GOT IT
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={exitSessionDialog || !tokenRemainingTime}
            onClose={() => deliveryStore.closeExitSessionDialog()}
            disableEscapeKeyDown={!tokenRemainingTime}
            maxWidth="sm"
            sx={classes.container}
            slotProps={{
              paper: { sx: { padding: '24px 0', textAlign: 'center', ...classes.container, ...classes.dialogPaper } },
            }}
          >
            <DialogTitle sx={classes.dialogTitle}>
              <span style={{ fontWeight: 600 }}>{!tokenRemainingTime ? 'Your token has expired!' : 'Manage Delivery Exit Confirmation'}</span>
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ ...classes.darkGrayText, ...classes.dialogBodyText }}>
                You will need to go through verification again to start another Manage Delivery session.
                {!!tokenRemainingTime && ' Please confirm if you want to exit this current session!'}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ ...classes.dialogActionsWrapper, justifyContent: 'center' }}>
              {!!tokenRemainingTime && (
                <Button
                  onClick={() => deliveryStore.closeExitSessionDialog()}
                  variant="outlined"
                  color="primary"
                  sx={{ ...classes.button, ...classes.dialogButton }}
                >
                  CANCEL
                </Button>
              )}
              <Button onClick={() => this.exitSession()} variant="contained" color="primary" sx={{ ...classes.button, ...classes.dialogButton }}>
                EXIT
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
        {(!settings?.hide_footer || settings.hide_footer === 'false') && (
          <FooterContainer client={client} shipment={shipment} settings={settings} />
        )}
        <Routes>
          <Route path="signature" element={<SignatureContainer />} />
        </Routes>
      </Container>
    );
  }
}

const EditShipmentCompose = withTabActive(compose(inject('store'), observer)(EditShipmentContainer));

// Wrapper component to provide router hooks to class component
function EditShipmentWrapper(props) {
  const params = useParams();
  const navigate = useNavigate();
  return <EditShipmentCompose {...props} match={{ params }} navigate={navigate} />;
}

export default withUseStyles(withMediaQuery()(EditShipmentWrapper));
